import { api, ApiError, downloadFile } from "../api.js";
import { esc, difficultyBadgeClass, siteLocationLabel, toast } from "../utils.js";
import { postCardHtml, attachPostCardHandlers } from "../postCard.js";
import { planCardHtml, attachPlanCardHandlers } from "../divePlanCard.js";
import { navigate } from "../app.js";
import { confirmAction } from "../confirmDialog.js";
import { linksSectionHtml, attachLinkHandlers } from "../links.js";
import { getCurrentUser, isSiteAdmin } from "../auth.js";
import {
  photoUploadHtml,
  attachPhotoUpload,
  infiniteGallery,
} from "../photoGallery.js";
import { tabSelectorHtml, attachTabSelector } from "../tabbedSection.js";
import { infiniteScroll } from "../infiniteScroll.js";
import { manageMenuHtml } from "../manageMenu.js";
import { signInPromptHtml, attachSignInLinks } from "../signInPrompt.js";
import { followSwitchHtml, attachFollowSwitch } from "../followSwitch.js";

export async function render(container, params) {
  const siteId = params.id;
  container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;

  let site, posts, upcomingCount, photoCount;
  try {
    // Counts only, for the tab labels. The events and photos themselves
    // page in when their tab is opened — this page used to ask for every
    // dive event in the system and filter here, which quietly lost
    // everything past the first page once the endpoint paginated.
    const [siteData, sitePlans, sitePosts, sitePhotos] = await Promise.all([
      api.get(`/dive-sites/${siteId}`),
      api.getPage("/dive-plans", {
        query: { dive_site_id: siteId, when: "upcoming" },
        limit: 1,
      }),
      api.get("/posts", { dive_site_id: siteId }),
      api.getPage(`/dive-sites/${siteId}/photos`, { limit: 1 }),
    ]);
    site = siteData;
    // Upcoming only. A count that included every dive ever logged here
    // would grow without bound and say nothing about whether there is
    // anything to join.
    upcomingCount = sitePlans.total ?? 0;
    posts = sitePosts;
    photoCount = sitePhotos.total ?? 0;
  } catch (err) {
    container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Dive site not found.</div></div>`;
    return;
  }

  const me = getCurrentUser();
  const canManage =
    me != null && (site.created_by?.id === me.id || isSiteAdmin());

  container.innerHTML = `
    <button class="btn btn-link ps-0 mb-2" id="back-btn"><i class="bi bi-arrow-left"></i> Back</button>
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <h1 class="display-heading h4 mb-0">${esc(site.name)}</h1>
          <div class="d-flex align-items-center gap-2">
            ${site.difficulty ? `<span class="${difficultyBadgeClass(site.difficulty)}">${esc(site.difficulty)}</span>` : ""}
            <button class="btn btn-sm btn-outline-secondary" type="button" id="export-site-btn"
                    title="Download this site for Subsurface">
              <i class="bi bi-download me-1"></i>Export
            </button>
            ${canManage
              ? manageMenuHtml(
                  [
                    { id: "edit-site-btn", icon: "bi-pencil", label: "Edit" },
                    { id: "delete-site-btn", icon: "bi-trash", label: "Delete", danger: true },
                  ],
                  { label: "Manage this dive site" }
                )
              : ""}
          </div>
        </div>
        ${site.description ? `<p class="mb-2 dc-user-text">${esc(site.description)}</p>` : ""}
        ${siteLocationLabel(site) ? `<div class="text-slate small mb-1"><i class="bi bi-geo-alt me-1"></i>${esc(siteLocationLabel(site))}</div>` : ""}
        <div class="text-slate small"><i class="bi bi-compass me-1"></i>${site.latitude.toFixed(4)}, ${site.longitude.toFixed(4)}</div>
        ${site.ocean ? `<div class="text-slate small"><i class="bi bi-globe-americas me-1"></i>${esc(site.ocean)}</div>` : ""}
        ${site.max_depth_m ? `<div class="text-slate small"><i class="bi bi-water me-1"></i>Max depth: ${site.max_depth_m}m</div>` : ""}
        ${site.scuba_sites_contributor ? `<div class="text-slate small mt-1" id="site-contributor"><i class="bi bi-person me-1"></i>Contributed by ${esc(site.scuba_sites_contributor)} on scuba-sites.com</div>` : ""}
        ${site.notes ? `<div class="mt-3" id="site-notes"><div class="small fw-semibold mb-1">Notes</div><div class="dc-user-text">${esc(site.notes)}</div></div>` : ""}
        ${followSwitchHtml({
          following: site.is_following,
          label: "Follow this dive site",
          help: "Posts about it appear in your Following feed.",
        })}
      </div>
    </div>
    ${me ? "" : signInPromptHtml(
      "Log in or sign up to post about this site, plan a dive here, or add photos and links.")}
    <div id="links-host"></div>
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div id="site-tabs-host"></div>
          <div id="site-tab-action"></div>
        </div>
        <div id="site-tab-panel"></div>
      </div>
    </div>`;

  let activeTab = "posts";
  // Both page in as you scroll; stopped when the tab changes, so a feed
  // for a tab nobody is looking at is not still fetching.
  let activeGallery = null;
  let activeEvents = null;

  /** Paints the active tab, its action button and its panel.
   *
   * Panels are rebuilt on switch rather than hidden, so handlers bind to
   * the elements actually on screen and a stale panel cannot accumulate
   * duplicate listeners. */
  function renderTabs() {
    const host = container.querySelector("#site-tabs-host");
    const panel = container.querySelector("#site-tab-panel");
    const action = container.querySelector("#site-tab-action");
    if (!host || !panel) return;

    // Stop the previous tab's feed before its panel — and the sentinel
    // it watches — is replaced. Done here rather than in each branch so
    // leaving a tab stops it, not only returning to it.
    activeEvents?.destroy();
    activeEvents = null;
    activeGallery?.destroy();
    activeGallery = null;

    host.innerHTML = tabSelectorHtml(
      [
        { id: "posts", label: "Posts", count: posts.length },
        { id: "events", label: "Dive Events", count: upcomingCount },
        { id: "photos", label: "Photos", count: photoCount },
      ],
      activeTab,
      { id: "site-tabs" }
    );
    attachTabSelector(host, (id) => {
      activeTab = id;
      renderTabs();
    });

    if (activeTab === "posts") {
      action.innerHTML = me
        ? `<button class="btn btn-sm btn-outline-primary" id="post-about-btn"><i class="bi bi-plus-lg me-1"></i>Post</button>`
        : `<a href="#/login" class="btn btn-sm btn-outline-primary" data-sign-in="login">Log in to post</a>`;
      panel.innerHTML = posts.length
        ? posts.map(postCardHtml).join("")
        : `<p class="text-slate mb-0">No posts about this site yet.</p>`;
      attachPostCardHandlers(panel);
      attachSignInLinks(action);
      action.querySelector("#post-about-btn")?.addEventListener("click", () => {
        // A site carries no cohort or event of its own, so the site is
        // the only context there is to pass.
        const q = new URLSearchParams();
        q.set("dive_site_id", site.id);
        navigate(`/create-post?${q.toString()}`);
      });
    } else if (activeTab === "events") {
      action.innerHTML = me
        ? `<button class="btn btn-sm btn-outline-primary" id="plan-here-btn"><i class="bi bi-plus-lg me-1"></i>Plan</button>`
        : `<a href="#/login" class="btn btn-sm btn-outline-primary" data-sign-in="login">Log in to plan a dive</a>`;
      attachSignInLinks(action);
      action.querySelector("#plan-here-btn")?.addEventListener("click", () => {
        // The site is the only context a dive site page carries, the
        // same way the Post button on this page passes it.
        const q = new URLSearchParams();
        q.set("dive_site_id", site.id);
        navigate(`/create-dive-plan?${q.toString()}`);
      });
      activeEvents = infiniteScroll(panel, {
        path: "/dive-plans",
        query: { dive_site_id: siteId },
        emptyMessage: "No dive events at this site yet.",
        render: (list, plans) => {
          const batch = document.createElement("div");
          batch.innerHTML = plans.map(planCardHtml).join("");
          attachPlanCardHandlers(batch);
          list.append(batch);
        },
      });
    } else {
      // Uploading needs an account; a button that only ever 401s is
      // worse than no button.
      action.innerHTML = getCurrentUser() ? photoUploadHtml("site-photo-add") : "";
      attachPhotoUpload(action, {
        id: "site-photo-add",
        endpoint: `/dive-sites/${siteId}/photos`,
        onUploaded: async () => {
          photoCount = (await api.getPage(`/dive-sites/${siteId}/photos`, { limit: 1 })).total ?? 0;
          renderTabs();
        },
      });
      const me = getCurrentUser();
      activeGallery = infiniteGallery(panel, {
        path: `/dive-sites/${siteId}/photos`,
        emptyMessage:
          "No photos of this site yet. Photos attached to posts tagged with this site show up in this gallery.",
        // Whoever uploaded it, or a moderator — the same rule the API
        // enforces.
        canDelete: (photo) => me != null && (photo.owner_id === me.id || isSiteAdmin()),
        onDeleted: async () => renderTabs(),
      });
    }
  }

  function renderLinks() {
    const host = container.querySelector("#links-host");
    const me = getCurrentUser();
    host.innerHTML = linksSectionHtml(site.links, {
      // Dive sites are shared reference data, so anyone signed in may
      // add one; only the person who added it (or a moderator) may take
      // it away. Mirrors the API.
      canAdd: me != null,
      canManage: (link) =>
        me != null && (link.created_by?.id === me.id || isSiteAdmin()),
    });
    attachLinkHandlers(host, {
      basePath: `/dive-sites/${siteId}/links`,
      reload: async () => {
        site = await api.get(`/dive-sites/${siteId}`);
        renderLinks();
      },
    });
  }
  renderLinks();

  container.querySelector("#back-btn").addEventListener("click", () => history.back());
  attachFollowSwitch(container, { path: `/dive-sites/${siteId}` });
  attachSignInLinks(container.querySelector("#sign-in-prompt"));

  container.querySelector("#export-site-btn").addEventListener("click", async () => {
    try {
      await downloadFile(`/dive-sites/${siteId}/export`);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not export this site.", "danger");
    }
  });

  container.querySelector("#edit-site-btn")?.addEventListener("click", () => {
    navigate(`/dive-site/${siteId}/edit`);
  });

  container.querySelector("#delete-site-btn")?.addEventListener("click", async () => {
    const confirmed = await confirmAction({
      title: "Delete this dive site?",
      body:
        "Posts and photos tagged with it stay, but lose the tag. Past dive " +
        "events here are deleted with it. A site with upcoming events cannot " +
        "be deleted.",
      detail: site.name,
      confirmLabel: "Delete",
    });
    if (!confirmed) return;
    try {
      await api.delete(`/dive-sites/${siteId}`);
      navigate("/dive-sites");
    } catch (err) {
      // A 409 explains which upcoming events are in the way, and that
      // message is worth showing verbatim.
      toast(err instanceof ApiError ? err.message : "Could not delete that site.", "danger");
    }
  });
  renderTabs();
}
